import type { WorkItem } from "@business-ops/domain";
import type { OpsAssistantMutationCommand, OpsAssistantResponse } from "./api.js";

export const WORK_MUTATION_STAGES = ["TARGETED", "CHANGING", "DONE", "FAILED"] as const;
export type WorkMutationStage = (typeof WORK_MUTATION_STAGES)[number];

export interface WorkMutationFeedback {
  operationId: string;
  workItemId: string;
  expectedVersion: number;
  stage: WorkMutationStage;
  accepted: boolean;
  observedVersion: number | null;
  failure: string | null;
}

export interface WorkMutationFeedbackClear {
  operationId: string;
  workItemId: null;
  stage: null;
}

export type WorkMutationFeedbackUpdate = WorkMutationFeedback | WorkMutationFeedbackClear;

export interface WorkMutationTarget {
  workItemId: string;
  expectedVersion: number;
}

type LocalWorkMutationStage = Exclude<WorkMutationStage, "DONE">;

export function workMutationTarget(command: OpsAssistantMutationCommand): WorkMutationTarget | null {
  return "workItemId" in command && "expectedVersion" in command
    ? { workItemId: command.workItemId, expectedVersion: command.expectedVersion }
    : null;
}

export function workMutationFeedback(
  command: OpsAssistantMutationCommand,
  operationId: string,
  stage: LocalWorkMutationStage
): WorkMutationFeedback | null {
  const target = workMutationTarget(command);
  return target ? {
    operationId,
    ...target,
    stage,
    accepted: false,
    observedVersion: null,
    failure: stage === "FAILED" ? "The task change was not accepted." : null
  } : null;
}

export function targetedWorkMutationFeedback(
  response: OpsAssistantResponse,
  operationId: string
): WorkMutationFeedback | null {
  return response.kind === "MUTATION_PREVIEW"
    ? workMutationFeedback(response.preview.command, operationId, "TARGETED")
    : null;
}

export function clearWorkMutationFeedback(operationId: string): WorkMutationFeedbackClear {
  return { operationId, workItemId: null, stage: null };
}

export function acceptedWorkMutationFeedback(
  command: OpsAssistantMutationCommand,
  operationId: string,
  returnedWorkItemId: string | null
): WorkMutationFeedback | null {
  const target = workMutationTarget(command);
  if (!target) return null;
  if (returnedWorkItemId !== target.workItemId) {
    return {
      operationId,
      ...target,
      stage: "FAILED",
      accepted: false,
      observedVersion: null,
      failure: "The saved task identity did not match this change."
    };
  }
  return {
    operationId,
    ...target,
    stage: "CHANGING",
    accepted: true,
    observedVersion: null,
    failure: null
  };
}

// WHY [BOL-WORK-MUTATION-OBSERVATION-001]: an HTTP success is acceptance, not
// proof that the visible canonical Task changed. Keep each exact turn + Work ID
// + expected version independent and say DONE only after a newer Board version
// is observed. Falsifier: refresh failure says DONE, two tasks hide each other,
// or a stale/mismatched Task is presented as changed.
export function observeWorkMutationFeedback(
  current: readonly WorkMutationFeedback[],
  workItems: readonly Pick<WorkItem, "id" | "version">[]
): readonly WorkMutationFeedback[] {
  const versions = new Map(workItems.map((item) => [item.id, item.version]));
  let changed = false;
  const next = current.map((entry) => {
    if (entry.stage === "DONE" || entry.stage === "FAILED") return entry;
    const observedVersion = versions.get(entry.workItemId);
    if (observedVersion === undefined || observedVersion < entry.expectedVersion) {
      changed = true;
      return failObserved(entry, observedVersion ?? null, "The canonical task is no longer at the targeted version.");
    }
    if (entry.stage === "TARGETED" && observedVersion !== entry.expectedVersion) {
      changed = true;
      return failObserved(entry, observedVersion, "The task changed before this preview was applied.");
    }
    if (entry.stage === "CHANGING" && entry.accepted && observedVersion > entry.expectedVersion) {
      changed = true;
      return { ...entry, stage: "DONE" as const, observedVersion, failure: null };
    }
    if (entry.stage === "CHANGING" && !entry.accepted && observedVersion !== entry.expectedVersion) {
      changed = true;
      return failObserved(entry, observedVersion, "The task changed before the save was accepted.");
    }
    if (entry.observedVersion !== observedVersion) {
      changed = true;
      return { ...entry, observedVersion };
    }
    return entry;
  });
  return changed ? next : current;
}

export function currentWorkMutationFeedback(
  current: readonly WorkMutationFeedback[],
  workItemId: string
): WorkMutationFeedback | null {
  return [...current].reverse().find((entry) => entry.workItemId === workItemId) ?? null;
}

export function applyWorkMutationFeedbackUpdate(
  current: readonly WorkMutationFeedback[],
  update: WorkMutationFeedbackUpdate
): readonly WorkMutationFeedback[] {
  if (update.stage === null) {
    const next = current.filter((entry) => entry.operationId !== update.operationId);
    return next.length === current.length ? current : next;
  }
  const index = current.findIndex((entry) => entry.operationId === update.operationId);
  if (index < 0) return [...current, update].slice(-80);
  const prior = current[index]!;
  if (prior.stage === "DONE" || prior.stage === "FAILED") return current;
  if (sameFeedback(prior, update)) return current;
  const next = [...current];
  next[index] = update;
  return next;
}

function failObserved(
  entry: WorkMutationFeedback,
  observedVersion: number | null,
  failure: string
): WorkMutationFeedback {
  return { ...entry, stage: "FAILED", observedVersion, failure };
}

function sameFeedback(left: WorkMutationFeedback, right: WorkMutationFeedback): boolean {
  return left.operationId === right.operationId
    && left.workItemId === right.workItemId
    && left.expectedVersion === right.expectedVersion
    && left.stage === right.stage
    && left.accepted === right.accepted
    && left.observedVersion === right.observedVersion
    && left.failure === right.failure;
}
